"use client"

import { useTranslations } from "next-intl"

import { StatsMetaline, type StatsMetalineItem } from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"

import type { LeadStats, AppointmentStats, QuoteStats } from "@/types"

interface DashboardStatsProps {
  leadStats: LeadStats | null
  appointmentStats: AppointmentStats | null
  quoteStats: QuoteStats | null
  isLoading: boolean
}

/**
 * Dashboard headline metrics rendered as the header metadata line (mono values,
 * hairline dividers) inside the page header. Same data wiring as before —
 * leads / appointments / active-quotes / conversion-rate — mapped onto the
 * shared {@link StatsMetaline} primitive. During load the values fall back to
 * inline skeletons so the line keeps its height.
 */
export function DashboardStats({ leadStats, appointmentStats, quoteStats, isLoading }: DashboardStatsProps) {
  const t = useTranslations("dashboard")

  // Calculate conversion rate (leads won / total leads)
  const conversionRate = leadStats?.by_status
    ? (() => {
        const wonStatus = leadStats.by_status.find(s => s.name.toLowerCase() === 'won')
        const wonCount = wonStatus?.count || 0
        const total = leadStats.total || 0
        return total > 0 ? ((wonCount / total) * 100).toFixed(1) : "0"
      })()
    : "0"

  const skeletonValue = <Skeleton className="h-[13px] w-12" />

  const items: StatsMetalineItem[] = [
    {
      key: "totalLeads",
      label: t("stats.totalLeads"),
      value: isLoading ? skeletonValue : (leadStats?.total ?? 0),
      sub: isLoading ? undefined : t("stats.totalLeadsHint", { count: leadStats?.this_month ?? 0 }),
    },
    {
      key: "appointments",
      label: t("stats.appointments"),
      value: isLoading ? skeletonValue : (appointmentStats?.this_week ?? 0),
      sub: isLoading
        ? undefined
        : t("stats.appointmentsHint", {
            today: appointmentStats?.today ?? 0,
            upcoming: appointmentStats?.upcoming ?? 0,
          }),
    },
    {
      key: "activeQuotes",
      label: t("stats.activeQuotes"),
      value: isLoading ? skeletonValue : (quoteStats?.by_status?.sent ?? 0),
      sub: isLoading ? undefined : t("stats.activeQuotesHint", { count: quoteStats?.expiring_soon ?? 0 }),
    },
    {
      key: "conversionRate",
      label: t("stats.conversionRate"),
      value: isLoading ? skeletonValue : `${conversionRate}%`,
      sub: isLoading ? undefined : t("stats.conversionRateHint"),
    },
  ]

  return <StatsMetaline items={items} />
}
